import { supabase } from '../lib/supabase'
import { invoiceAmounts } from '../lib/invoiceMaths'
import { isValidAbn } from './buyerService'
import { transitionDealStatus } from '../lib/dealStatusService'
import { calculateCommission } from './commissionReconService'
import type { User } from '../types'

/**
 * Sale Details (Workspace) - the outflow mirror of Purchase Details (Workspace).
 *
 * The sales team picks a deal out of the `deal_pending` column and finishes the sale here.
 * Until now `deal_pending` deals had a sales proposal and nowhere to complete against it.
 *
 *     deal_pending  ->  [Create Final Sale]  ->  sold_in_transit | sold_delivered
 *
 * The one thing this file exists to protect is that Create Final Sale is a single event.
 * Commission, the margin scheme credit and the AR record all key off the write to
 * `deal_status` - not off a page load, a report refresh, or three separate buttons that
 * happen to agree. If any of those three ever starts being triggered somewhere else, the
 * figures drift apart and nobody finds out until a BAS or a commission run disagrees.
 */

export type ShippingParty = 'DirectAutoShips' | 'BuyerShips'

export interface SaleWorkspace {
  id: string
  dealId: string
  buyerName: string
  buyerAbn: string
  /** Compulsory on a tax invoice to a dealer buyer - Winnie, section 5.2. */
  buyerLmct: string
  buyerAddress: string
  buyerEmail: string
  buyerMobile: string
  shippingParty: ShippingParty | null
  dealNumber: string
  contractGeneratedAt: string | null
  taxInvoiceGeneratedAt: string | null
  contractPath: string | null
  taxInvoicePath: string | null
  documentsVoid: boolean
  finalisedAt: string | null
  coolingOffExpiresAt: string | null
  isAmended: boolean
  isVoided: boolean
  ppsrDischargedAt: string | null
}

const row2ws = (r: Record<string, unknown>): SaleWorkspace => ({
  id: String(r.id),
  dealId: String(r.deal_id),
  buyerName: String(r.buyer_name || ''),
  buyerAbn: String(r.buyer_abn || ''),
  buyerLmct: String(r.buyer_lmct || ''),
  buyerAddress: String(r.buyer_address || ''),
  buyerEmail: String(r.buyer_contact_email || ''),
  buyerMobile: String(r.buyer_contact_mobile || ''),
  shippingParty: (r.shipping_party as ShippingParty) || null,
  dealNumber: String(r.deal_number || ''),
  contractGeneratedAt: (r.contract_generated_at as string) || null,
  taxInvoiceGeneratedAt: (r.tax_invoice_generated_at as string) || null,
  contractPath: (r.contract_document_path as string) || null,
  taxInvoicePath: (r.tax_invoice_document_path as string) || null,
  documentsVoid: Boolean(r.documents_void),
  finalisedAt: (r.finalised_at as string) || null,
  coolingOffExpiresAt: (r.cooling_off_expires_at as string) || null,
  isAmended: Boolean(r.is_amended),
  isVoided: Boolean(r.is_voided),
  ppsrDischargedAt: (r.ppsr_discharged_at as string) || null,
})

/** Seven days from the contract, per section 7. Stored, not recomputed on every read. */
export const COOLING_OFF_DAYS = 7

/**
 * The workspace for a deal, creating it on first open.
 *
 * Opening the screen is not an act with consequences, so this deliberately does not draw a
 * deal number: a number is allocated when the sale is finalised. Otherwise every deal
 * somebody merely looked at would burn one and the sequence would read like lost sales.
 */
export async function openSaleWorkspace(dealId: string, userId: string | null): Promise<SaleWorkspace> {
  const { data: found, error: readErr } = await supabase
    .from('sale_workspaces')
    .select('*')
    .eq('deal_id', dealId)
    .maybeSingle()
  if (readErr) throw readErr
  if (found) return row2ws(found as Record<string, unknown>)

  /* Pre-fill from the buyer already attached to the deal at proposal time, so the sales
     team is confirming what the SP said rather than typing it again. */
  const { data: deal } = await supabase
    .from('deals')
    .select('buyer_name, buyer_company, buyer_email, buyer_mobile, buyer_abn, buyer_lmct, buyer_address, transport_arranged_by')
    .eq('id', dealId)
    .maybeSingle()

  const d = (deal || {}) as Record<string, unknown>
  const arranged = String(d.transport_arranged_by || '').toLowerCase()

  const { data: made, error } = await supabase
    .from('sale_workspaces')
    .insert([
      {
        deal_id: dealId,
        buyer_name: d.buyer_name || d.buyer_company || null,
        buyer_abn: d.buyer_abn || null,
        buyer_lmct: d.buyer_lmct || null,
        buyer_address: d.buyer_address || null,
        buyer_contact_email: d.buyer_email || null,
        buyer_contact_mobile: d.buyer_mobile || null,
        /* The pipeline already asked who arranges transport when the proposal was built.
           'dealer' there means the buying dealer arranges it, which is BuyerShips here. */
        shipping_party: arranged === 'dealer' ? 'BuyerShips' : arranged ? 'DirectAutoShips' : null,
        created_by: userId,
      },
    ])
    .select('*')
    .single()
  if (error) throw error
  return row2ws(made as Record<string, unknown>)
}

export async function saveSaleWorkspace(
  dealId: string,
  patch: Partial<{
    buyerName: string
    buyerAbn: string
    buyerLmct: string
    buyerAddress: string
    buyerEmail: string
    buyerMobile: string
    shippingParty: ShippingParty | null
  }>,
): Promise<void> {
  const out: Record<string, unknown> = { updated_at: new Date().toISOString() }
  if (patch.buyerName !== undefined) out.buyer_name = patch.buyerName || null
  if (patch.buyerAbn !== undefined) out.buyer_abn = patch.buyerAbn || null
  if (patch.buyerLmct !== undefined) out.buyer_lmct = patch.buyerLmct || null
  if (patch.buyerAddress !== undefined) out.buyer_address = patch.buyerAddress || null
  if (patch.buyerEmail !== undefined) out.buyer_contact_email = patch.buyerEmail || null
  if (patch.buyerMobile !== undefined) out.buyer_contact_mobile = patch.buyerMobile || null
  if (patch.shippingParty !== undefined) out.shipping_party = patch.shippingParty

  const { error } = await supabase.from('sale_workspaces').update(out).eq('deal_id', dealId)
  if (error) throw error
}

/* --------------------------------------------------------------------------------------
 * The gate
 * ------------------------------------------------------------------------------------ */

export interface FinalSaleGate {
  ok: boolean
  problems: string[]
}

/**
 * Section 3's validation, in the spec's order, and all of it before anything is written.
 *
 * The ABN checksum matters more than it looks. There is no registry lookup in this action,
 * so the checksum is the only thing standing between a typo and two legal documents issued
 * to a buyer who does not exist. Catching it here is free; catching it after the contract
 * and the tax invoice carry the wrong number is not.
 */
export function checkFinalSale(input: {
  buyerAbn?: string | null
  buyerName?: string | null
  shippingParty?: ShippingParty | null
  salePrice?: number | null
  alreadyFinalised?: boolean
  isVoided?: boolean
}): FinalSaleGate {
  const problems: string[] = []

  if (input.alreadyFinalised) {
    return {
      ok: false,
      problems: ['This sale has already been finalised. Changes go through an amendment, not a second Create Final Sale.'],
    }
  }
  if (input.isVoided) {
    return {
      ok: false,
      problems: ['This sale was voided. The vehicle is back in stock and needs a new sale rather than this one being finished.'],
    }
  }

  const abn = String(input.buyerAbn || '').replace(/\D/g, '')
  if (!abn) {
    problems.push('Enter the buyer’s ABN. Direct Auto sells wholesale to ABN-holding buyers only.')
  } else if (abn.length !== 11) {
    problems.push('An ABN is 11 digits. This one has ' + abn.length + '.')
  } else if (!isValidAbn(abn)) {
    problems.push('That ABN fails its checksum, so it is not a real ABN. Check it against the buyer’s paperwork.')
  }

  if (!String(input.buyerName || '').trim()) {
    problems.push('Enter the buyer’s name — it goes on both documents.')
  }

  if (!input.shippingParty) {
    problems.push('Choose who is shipping. It decides whether this sale lands on Sold — In Transit or Sold — Delivered.')
  }

  const price = Number(input.salePrice)
  if (!Number.isFinite(price) || price <= 0) {
    problems.push(
      'There is no sale price on this deal. The price comes from the sales proposal, so go back through that step rather than setting it here.',
    )
  }

  return { ok: problems.length === 0, problems }
}

/* --------------------------------------------------------------------------------------
 * Create Final Sale
 * ------------------------------------------------------------------------------------ */

export interface FinalSaleResult {
  ok: boolean
  message: string
  dealNumber?: string
  status?: string
}

/**
 * One action, one status write, three downstream calculations keyed off it.
 *
 * Order matters here and is not arbitrary:
 *
 *  1. The gate runs first, so nothing is written for a sale that cannot complete.
 *  2. The deal number is drawn from the database sequence, not counted in the client.
 *  3. The documents are generated before the status moves. If document generation fails,
 *     the deal is still `deal_pending` and the sale can be retried - which is recoverable.
 *     Moving the status first and failing on the documents would leave a car marked sold
 *     with no contract and no invoice, which is not.
 *  4. The status move is last, and it goes through `transitionDealStatus` rather than
 *     writing `deal_status` here. That service owns the permission check, the precondition
 *     (a car does not leave the yard without a proposal on it), the lifecycle date stamps,
 *     the status log and the workflow triggers. A direct write would skip all five, which
 *     is exactly the hole found on the payment side where two functions set `paid` by hand
 *     and bypassed the PPSR gate entirely.
 */
export async function createFinalSale(input: {
  dealId: string
  user: User | null
}): Promise<FinalSaleResult> {
  const { dealId, user } = input
  const userId = user?.id || null

  const [{ data: wsRow }, { data: dealRow }] = await Promise.all([
    supabase.from('sale_workspaces').select('*').eq('deal_id', dealId).maybeSingle(),
    supabase.from('deals').select('*').eq('id', dealId).maybeSingle(),
  ])

  if (!wsRow || !dealRow) {
    return { ok: false, message: 'This deal could not be read, so nothing has been finalised. Try again.' }
  }

  const ws = row2ws(wsRow as Record<string, unknown>)
  const deal = dealRow as Record<string, unknown>
  const salePrice = Number(deal.sale_price)

  const gate = checkFinalSale({
    buyerAbn: ws.buyerAbn,
    buyerName: ws.buyerName,
    shippingParty: ws.shippingParty,
    salePrice,
    alreadyFinalised: Boolean(ws.finalisedAt),
    isVoided: ws.isVoided,
  })
  if (!gate.ok) return { ok: false, message: gate.problems.join(' ') }

  const now = new Date()
  const nowIso = now.toISOString()

  let dealNumber = ws.dealNumber
  if (!dealNumber) {
    const { data: num, error: numErr } = await supabase.rpc('next_sale_deal_number')
    if (numErr || !num) {
      return {
        ok: false,
        message: 'A deal number could not be allocated, so nothing has been finalised. Try again.',
      }
    }
    dealNumber = String(num)
  }

  const status = ws.shippingParty === 'DirectAutoShips' ? 'sold_in_transit' : 'sold_delivered'

  /* Both dates are settled before the documents are drawn, because both are printed on
     them. Working them out afterwards would produce paper that disagrees with the record. */
  const coolingOff = new Date(now.getTime() + COOLING_OFF_DAYS * 24 * 60 * 60 * 1000).toISOString()
  const dueDate = new Date(now.getTime() + AR_TERMS_DAYS * 24 * 60 * 60 * 1000).toISOString()

  /* Documents first - see the note above about which failure is recoverable. */
  const docs = await generateSaleDocuments({ dealId, dealNumber, dueDate, coolingOffExpiresAt: coolingOff })
  if (!docs.ok) {
    return {
      ok: false,
      message:
        'The sale documents could not be generated, so the sale has not been finalised and the deal is untouched. ' +
        docs.message,
    }
  }

  const { error: wsErr } = await supabase
    .from('sale_workspaces')
    .update({
      deal_number: dealNumber,
      contract_generated_at: nowIso,
      tax_invoice_generated_at: nowIso,
      contract_document_path: docs.contractPath,
      tax_invoice_document_path: docs.taxInvoicePath,
      finalised_at: nowIso,
      cooling_off_expires_at: coolingOff,
      updated_at: nowIso,
    })
    .eq('deal_id', dealId)
  if (wsErr) {
    return { ok: false, message: 'The sale documents were generated but the workspace could not be saved: ' + wsErr.message }
  }

  /* The AR record. `invoices` is this platform's ARRecord - same fields, same chase list. */
  const arResult = await createArRecord({ dealId, ws, dealNumber, salePrice, userId, dueDate })

  /* The workspace for whoever moves the car, opened by the same action that decided who. */
  if (ws.shippingParty === 'DirectAutoShips') {
    await supabase.from('freight_out_workspaces').upsert(
      [{ deal_id: dealId, status: 'NotStarted', who: userId }],
      { onConflict: 'deal_id' },
    )
  } else {
    await supabase.from('pickup_workspaces').upsert([{ deal_id: dealId }], { onConflict: 'deal_id' })
  }

  /* Last, and deliberately last: the single event everything downstream keys off. */
  const moved = await transitionDealStatus(
    dealId,
    String(deal.deal_status || 'deal_pending') as never,
    status as never,
    userId || undefined,
    user,
    'Create Final Sale — deal ' + dealNumber,
  )
  if (!moved.success) {
    return {
      ok: false,
      message:
        'The documents and the AR record were created but the vehicle status could not be moved: ' +
        (moved.message || 'the transition was refused') +
        ' The sale is incomplete — sort that out rather than pressing this again.',
    }
  }

  /*
   * Commission, struck here rather than waiting for somebody to open a report.
   *
   * Commission spec v2 section 3 names this exact event: "Fired by the Create Final Sale
   * action in Sale_Details_Workspace_Build_Spec.md section 3 - that's the concrete UI event,
   * not a status change happening on its own." Until the 14 September spec audit it was not
   * wired at all: `calculateCommission` had a single caller, the manual Strike button on the
   * Commission Report, so a vehicle that sold and that nobody opened that screen for carried
   * no commission record - while the screen said commission is calculated at Sold.
   *
   * Deliberately AFTER the status move and deliberately non-fatal. The calculation refuses
   * for reasons that are not failures of this sale - no sale price yet, a cost type with no
   * standard rate configured - and a sale that is otherwise complete must not be reported as
   * broken because of it. Whatever happens is said out loud in the message rather than
   * swallowed, and the Strike button remains for the cases that need a person.
   *
   * It is safe to run twice: the record is inserted, never upserted, and a locked record is
   * refused by both the service and a database trigger.
   */
  let commissionNote: string
  try {
    const struck = await calculateCommission(dealId)
    commissionNote = struck.ok
      ? ' ' + struck.message
      : ' Commission was not struck: ' + struck.message + ' Strike it from the Commission Report once that is sorted.'
  } catch (err) {
    commissionNote =
      ' Commission was not struck: ' +
      (err instanceof Error ? err.message : 'the calculation could not be run') +
      ' Strike it from the Commission Report.'
  }

  const where = status === 'sold_in_transit' ? 'Sold — In Transit' : 'Sold — Delivered'
  return {
    ok: true,
    dealNumber,
    status,
    message:
      'Deal ' +
      dealNumber +
      ' finalised. The contract and tax invoice are generated, the vehicle is ' +
      where +
      ', and ' +
      (arResult.ok ? 'the AR record is open.' : 'the AR record could not be created — ' + arResult.message) +
      commissionNote,
  }
}

/** Framework C.3 - the same fourteen days the existing invoice service raises against. */
const AR_TERMS_DAYS = 14

/**
 * The AR record, created at the same moment as everything else.
 *
 * Three things here are deliberately not this file's own opinion:
 *
 *  - The money comes from `invoiceAmounts`, the one definition of what a tax invoice adds
 *    up to. A wholesale price in this business is GST-INCLUSIVE, so the GST is extracted
 *    from the sale price rather than added on top of it. Working that out again here is
 *    how the platform ended up with two answers to the same question before.
 *  - The status is `sent`, not something new. Sales (AR) and every reminder in
 *    `arService` chase `sent | overdue | partial | disputed`; a status outside that list
 *    would produce an invoice nobody ever asks the buyer to pay.
 *  - The terms are fourteen days, matching `invoiceService`, because the day-1, day-3,
 *    day-7 escalations are all counted from this due date.
 *
 * Never throws: a missing AR row is a problem somebody can fix from Sales (AR), while an
 * exception here would strand a sale that has already generated its documents.
 */
async function createArRecord(input: {
  dealId: string
  ws: SaleWorkspace
  dealNumber: string
  salePrice: number
  userId: string | null
  dueDate: string
}): Promise<{ ok: boolean; message: string }> {
  const { dealId, ws, dealNumber, salePrice, userId, dueDate } = input
  try {
    const { data: already } = await supabase
      .from('invoices')
      .select('id')
      .eq('deal_id', dealId)
      .limit(1)
    if (already && already.length) {
      return { ok: true, message: 'An AR record already existed for this vehicle and was left alone.' }
    }

    const now = new Date()
    const amounts = invoiceAmounts({ gross: salePrice })

    const { data: made, error } = await supabase
      .from('invoices')
      .insert([
        {
          deal_id: dealId,
          invoice_number: dealNumber,
          invoice_date: now.toISOString(),
          due_date: dueDate,
          buyer_name: ws.buyerName || null,
          buyer_abn: ws.buyerAbn || null,
          buyer_lmct: ws.buyerLmct || null,
          buyer_address: ws.buyerAddress || null,
          buyer_email: ws.buyerEmail || null,
          buyer_mobile: ws.buyerMobile || null,
          purchase_price: amounts.gross,
          discount_amount: amounts.discount,
          gst_amount: amounts.gstAmount,
          net_price: amounts.netPrice,
          tax_rate: amounts.taxRate,
          total_amount: amounts.total,
          payment_amount_received: 0,
          status: 'sent',
          created_by_user_id: userId,
        },
      ])
      .select('id')
      .single()
    if (error) throw error

    /* The deal carries the invoice it is being chased on. Without this the Deal Pipeline
       and Sales (AR) hold the same fact separately and only one of them gets updated. */
    if (made?.id) {
      const { error: linkError } = await supabase
        .from('deals')
        .update({ invoice_id: made.id })
        .eq('id', dealId)
      if (linkError) console.error('Could not link the invoice back to the deal', linkError)
    }

    return { ok: true, message: 'AR record created.' }
  } catch (e) {
    console.error('Could not create the AR record for this sale', e)
    return { ok: false, message: (e as Error).message || 'it could not be written.' }
  }
}

/**
 * Generate the contract and the tax invoice.
 *
 * Lives behind one call so the caller cannot generate one without the other - section 3
 * says both, in one atomic action, and a sale carrying a contract but no invoice would
 * satisfy nobody.
 */
export async function generateSaleDocuments(input: {
  dealId: string
  dealNumber: string
  amended?: boolean
  dueDate?: string
  coolingOffExpiresAt?: string
}): Promise<{ ok: boolean; message: string; contractPath?: string; taxInvoicePath?: string }> {
  try {
    const { data: sess } = await supabase.auth.getSession()
    const token = sess.session?.access_token
    const res = await fetch('/api/deals/sale-documents', {
      method: 'POST',
      headers: token
        ? { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token }
        : { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        dealId: input.dealId,
        dealNumber: input.dealNumber,
        amended: Boolean(input.amended),
        dueDate: input.dueDate || null,
        coolingOffExpiresAt: input.coolingOffExpiresAt || null,
      }),
    })
    const body = await res.json().catch(() => null)
    if (!res.ok || !body || !body.success) {
      return { ok: false, message: (body && body.error) || 'The document service did not answer.' }
    }
    return {
      ok: true,
      message: 'Documents generated.',
      contractPath: body.contractPath,
      taxInvoicePath: body.taxInvoicePath,
    }
  } catch (e) {
    return { ok: false, message: (e as Error).message || 'The document service could not be reached.' }
  }
}

/** A signed URL for a stored sale document, or null when there is nothing filed. */
export async function saleDocumentUrl(path: string | null): Promise<string | null> {
  if (!path) return null
  try {
    const { data, error } = await supabase.storage.from('sale-documents').createSignedUrl(path, 60 * 10)
    if (error) throw error
    return data?.signedUrl || null
  } catch (e) {
    console.error('Could not sign the sale document URL', e)
    return null
  }
}

/* ---------------------------------------------------------------------------
 * Moving the car: freight out, or the buyer collecting.
 *
 * Sale Details spec, section 10: the outflow mirror of the inflow side's Book Inspection
 * card. The two tables have existed since the 13 Sep migration, and Create Final Sale
 * opens the right one - but nothing has ever written to them since, so every freight job
 * has sat on NotStarted and `delivered_at` has never been set on any vehicle.
 *
 * That is not only a gap in the screen. Winnie's 13 Sep note asks Sales Performance for
 * average transit days, measured from these timestamps; a metric computed over a column
 * nothing writes reads as "no delays" rather than as "not tracked", which is the more
 * expensive of the two mistakes.
 * ------------------------------------------------------------------------- */

export type FreightStatus = 'NotStarted' | 'QuotesRequested' | 'Booked' | 'InTransit' | 'Delivered' | 'Cancelled'

export interface FreightOutWorkspace {
  dealId: string
  status: FreightStatus
  whereItStands: string
  carrier: string
  quoteRequestedAt: string | null
  bookedAt: string | null
  deliveredAt: string | null
  cancelledReason: string
  createdAt: string | null
}

export interface PickupWorkspace {
  dealId: string
  pickupConfirmedAt: string | null
  confirmedBy: string
  cancelledReason: string
  createdAt: string | null
}

/**
 * The carriers offered on the freight-out card.
 *
 * Winnie, 15 September: "this Admin Transport Panel (CarLink, Dipeana, ENJ, Tiempo, Jay Cee
 * Transport) should be the single source of truth for carriers, and section 10's freight-out
 * rate table should reference carriers from here (filtered to coverage in [FreightOut, Both])
 * rather than maintaining its own separate carrier list — the same 'don't duplicate a registry'
 * principle as the status registry."
 *
 * This replaces a hardcoded `KNOWN_CARRIERS = ['Ace Freighters', 'Tiempo Transport']`, which
 * named one carrier that is not on the panel at all and missed four that are. Free text is
 * still allowed on the field: a one-off carrier for one awkward delivery should not require an
 * Admin edit first.
 *
 * Inactive carriers are excluded — deactivating one on the panel is how a carrier stops being
 * offered — but a job that already names an inactive carrier keeps showing it, because the
 * field is free text and nothing rewrites history.
 */
export async function loadFreightCarrierNames(
  direction: 'FreightIn' | 'FreightOut' = 'FreightOut'
): Promise<string[]> {
  const { data, error } = await supabase
    .from('transport_providers')
    .select('name, coverage, active')
    .eq('active', true)
    .order('name')
  if (error) throw error
  return (data || [])
    .filter((r) => {
      const c = (r as { coverage?: string }).coverage || 'Both'
      return c === 'Both' || c === direction
    })
    .map((r) => String((r as { name?: string }).name || ''))
    .filter(Boolean)
}

export async function loadFreightOut(dealId: string): Promise<FreightOutWorkspace | null> {
  const { data, error } = await supabase
    .from('freight_out_workspaces')
    .select('*')
    .eq('deal_id', dealId)
    .maybeSingle()
  if (error) throw error
  if (!data) return null
  const r = data as Record<string, unknown>
  return {
    dealId,
    status: (r.status as FreightStatus) || 'NotStarted',
    whereItStands: String(r.where_it_stands || ''),
    carrier: String(r.carrier || ''),
    quoteRequestedAt: (r.quote_requested_at as string) || null,
    bookedAt: (r.booked_at as string) || null,
    deliveredAt: (r.delivered_at as string) || null,
    cancelledReason: String(r.cancelled_reason || ''),
    createdAt: (r.created_at as string) || null,
  }
}

export async function loadPickup(dealId: string): Promise<PickupWorkspace | null> {
  const { data, error } = await supabase
    .from('pickup_workspaces')
    .select('*')
    .eq('deal_id', dealId)
    .maybeSingle()
  if (error) throw error
  if (!data) return null
  const r = data as Record<string, unknown>
  return {
    dealId,
    pickupConfirmedAt: (r.pickup_confirmed_at as string) || null,
    confirmedBy: String(r.confirmed_by || ''),
    cancelledReason: String(r.cancelled_reason || ''),
    createdAt: (r.created_at as string) || null,
  }
}

/**
 * Record where the freight has got to.
 *
 * The timestamp for a stage is stamped when the stage is first reached and never restamped,
 * so a job corrected back a step and forward again keeps the time it actually happened.
 * `delivered_at` is the one every transit figure is measured to, which is precisely why it
 * must not move every time someone re-saves the card.
 */
export async function saveFreightOut(
  dealId: string,
  patch: { status?: FreightStatus; whereItStands?: string; carrier?: string; cancelledReason?: string },
  userId: string | null = null,
): Promise<FreightOutWorkspace | null> {
  const current = await loadFreightOut(dealId)
  const nowIso = new Date().toISOString()
  const status = patch.status ?? current?.status ?? 'NotStarted'

  const update: Record<string, unknown> = {
    deal_id: dealId,
    status,
    where_it_stands: patch.whereItStands ?? current?.whereItStands ?? '',
    carrier: patch.carrier ?? current?.carrier ?? '',
    cancelled_reason: patch.cancelledReason ?? current?.cancelledReason ?? '',
    who: userId,
    updated_at: nowIso,
  }
  if (status === 'QuotesRequested' && !current?.quoteRequestedAt) update.quote_requested_at = nowIso
  if (status === 'Booked' && !current?.bookedAt) update.booked_at = nowIso
  if (status === 'Delivered' && !current?.deliveredAt) update.delivered_at = nowIso

  const { error } = await supabase
    .from('freight_out_workspaces')
    .upsert([update], { onConflict: 'deal_id' })
  if (error) throw error
  return loadFreightOut(dealId)
}

/** The buyer collected it: who took it, and when. Three fields, because there are three facts. */
export async function confirmPickup(
  dealId: string,
  who: string,
  userId: string | null = null,
): Promise<PickupWorkspace | null> {
  const current = await loadPickup(dealId)
  const { error } = await supabase.from('pickup_workspaces').upsert(
    [{
      deal_id: dealId,
      /* Not restamped on a correction to the name, for the same reason as delivered_at. */
      pickup_confirmed_at: current?.pickupConfirmedAt || new Date().toISOString(),
      confirmed_by: who,
      confirmed_by_staff: userId,
      updated_at: new Date().toISOString(),
    }],
    { onConflict: 'deal_id' },
  )
  if (error) throw error
  return loadPickup(dealId)
}

/** The pickup fell through. Its own field, so a no-show reads differently from a freight delay. */
export async function recordPickupFailure(dealId: string, reason: string): Promise<void> {
  const { error } = await supabase
    .from('pickup_workspaces')
    .upsert([{ deal_id: dealId, cancelled_reason: reason, updated_at: new Date().toISOString() }], { onConflict: 'deal_id' })
  if (error) throw error
}
